import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadFile } from 'antd-v5';
import { RcFile } from 'antd-v5/lib/upload';
import Upload, { UploadFileState } from 'components/FileUploadButton/Upload';
import {
  getClaimsUploadAttachments,
  addClaimUploadAttachment,
  removeClaimUploadAttachment,
  fetchClaimDocumentUploadUrlRequestSaga,
  getOrderClaimForm,
  setOrderClaimForm,
} from 'domains/order/Order.store';

const getExtension = (fileName: string) => {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
};

const toBase64 = (rawFile: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(rawFile);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

const ClaimsFormAttachFiles = () => {
  const { t } = useTranslation();
  const allAttachments = useSelector(getClaimsUploadAttachments);
  const claimForm = useSelector(getOrderClaimForm);
  const formAttachments = claimForm.attachments;
  const dispatch = useDispatch();
  const presentableAttachments: UploadFile[] = useMemo(
    () =>
      Array.from(allAttachments.values()).map((attachment: UploadFileState) => {
        return {
          uid: attachment.uidLocal,
          name: attachment.fileName,
          fileName: attachment.fileName,
          status: attachment.status,
          error: attachment.error && { message: attachment.error },
          url: '-',
        };
      }),
    [allAttachments],
  );

  const handleUpload = async (file: RcFile): Promise<void> => {
    const { uid, name, size } = file;
    const MAX_SIZE_MB = 20;
    const isTooLarge = size / 1024 / 1024 > MAX_SIZE_MB;

    if (isTooLarge) {
      dispatch(
        addClaimUploadAttachment({
          uidLocal: uid,
          fileName: name,
          base64: '',
          status: 'error',
          extension: getExtension(name),
          error: t('common.file_upload.error.max_size', 'File size exceeds {{MAX_SIZE_MB}}MB.', { MAX_SIZE_MB }),
        }),
      );
      return;
    }

    const setFileToStore = (base64File?: string) => {
      dispatch(
        addClaimUploadAttachment({
          uidLocal: uid,
          fileName: name,
          base64: base64File,
          status: 'uploading',
          extension: getExtension(name),
        }),
      );
    };

    //with big files, conversion to base64 takes some time and the screen is not updated with 'uploading' state of the file right away
    setFileToStore();
    const base64 = await toBase64(file);
    setFileToStore(base64);
    dispatch(fetchClaimDocumentUploadUrlRequestSaga({ fileName: name }));
  };

  const handleRemove = (file: UploadFile) => {
    dispatch(removeClaimUploadAttachment(file.name));
    dispatch(
      setOrderClaimForm({
        ...claimForm,
        attachments: claimForm.attachments?.filter((attachment) => attachment.fileName !== file.name),
      }),
    );
  };

  useEffect(() => {
    const uploadedFiles = Array.from(allAttachments.values()).filter((attachment) => attachment.status === 'done');
    const isAnyNewAttachmentUploaded = uploadedFiles.some(
      (attachment) => !formAttachments?.some((formAttachment) => formAttachment.key === attachment.key),
    );
    if (isAnyNewAttachmentUploaded) {
      dispatch(
        setOrderClaimForm({
          ...claimForm,
          attachments: uploadedFiles.map((attachment) => {
            return {
              key: attachment.key ?? '',
              fileName: attachment.fileName,
              extension: getExtension(attachment.fileName),
            };
          }),
        }),
      );
    }
    // eslint-disable-next-line
  }, [allAttachments]);

  return (
    <Upload
      handleUpload={handleUpload}
      showUploadList
      fileList={presentableAttachments}
      onRemove={handleRemove}
      fileListType={'diagonal'}
    />
  );
};

export default ClaimsFormAttachFiles;
